import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { CopyField } from '../components/CopyField';
import { EarningsChart } from '../components/EarningsChart';
import { DataTable, type Column } from '../components/DataTable';
import { Icon } from '../components/Icons';
import { fmtDate, fmtDollars, fmtNum, fmtUSD } from '../lib/format';
import type { User360Collection, User360Dto, User360CollectionItem } from '../api/types';

export function User360Screen() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['user360', id],
    queryFn: () => insightsApi.user(id!),
    enabled: !!id,
  });

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Link
        to="/users"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 13, textDecoration: 'none' }}
      >
        <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}><Icon.Arrow /></span>
        Back to users
      </Link>

      {isLoading && <Card padding={28}><span style={{ color: 'var(--ink-3)' }}>Loading user…</span></Card>}
      {error && (
        <Card padding={20}>
          <span style={{ color: 'var(--bad)', fontSize: 13 }}>{(error as Error).message}</span>
        </Card>
      )}
      {data && <UserBody u={data} />}
    </div>
  );
}

function UserBody({ u }: { u: User360Dto }) {
  const pnl = parseFloat(u.portfolio.unrealizedPnl);
  const chart = u.portfolio.series.map((p) => ({
    label: fmtShort(p.recordedAt),
    amountCents: Math.round(parseFloat(p.totalMarketValue ?? '0') * 100),
  }));

  return (
    <>
      {/* Profile */}
      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 22 }}>
            User #{u.profile.id}
          </div>
          {u.premium.active ? <Pill tone="good" size="md">Premium</Pill> : <Pill size="md">Free</Pill>}
          <Pill size="md">{u.profile.platform}</Pill>
          {u.profile.country && <Pill size="md">{u.profile.country}</Pill>}
          <Pill size="md">{u.profile.preferredLang.toUpperCase()}</Pill>
          <Pill size="md">{u.profile.primaryCurrency}</Pill>
          {u.profile.isAnonymous && <Pill tone="warn" size="md">Anonymous</Pill>}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
            Joined {fmtDate(u.profile.createdAt)}
          </span>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <CopyField label="Device UUID" value={u.profile.deviceUuid} />
          {u.premium.active && u.premium.productId && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              Plan: <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{u.premium.productId}</span>
              {u.premium.expiresAt && <> · expires {fmtDate(u.premium.expiresAt)}</>}
            </div>
          )}
        </div>
      </Card>

      {/* Portfolio KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <KpiCard
          label="Portfolio value"
          value={parseFloat(u.portfolio.totalMarketValue)}
          format={(n) => fmtDollars(n)}
          accent
        />
        <KpiCard label="Cards held" value={u.portfolio.quantity} sublabel={`${u.portfolio.itemCount} listings`} />
        <KpiCard label="Collections" value={u.collections.length} />
        <KpiCard
          label="Unrealized P/L"
          value={pnl}
          format={(n) => fmtDollars(n)}
          delta={u.portfolio.unrealizedPnlPct}
          sublabel={`cost ${fmtDollars(u.portfolio.totalAcquiredCost)}`}
        />
      </div>

      {/* Portfolio chart */}
      <Card padding={18}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16, marginBottom: 10 }}>
          Portfolio value over time
        </div>
        {chart.length > 1 ? (
          <EarningsChart data={chart} />
        ) : (
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '30px 0', textAlign: 'center' }}>
            Not enough snapshots yet — daily history accrues from the 23:00 UTC snapshot.
          </div>
        )}
      </Card>

      {/* Attribution + earnings */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
            Attribution
          </div>
          {u.attribution ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <Line label="Creator">
                {u.attribution.creator ? (
                  <Link to={`/users`} style={{ color: 'var(--brand)', textDecoration: 'none' }}>
                    {u.attribution.creator.name} · @{u.attribution.creator.handle}
                  </Link>
                ) : '—'}
              </Line>
              <Line label="Code"><span style={{ fontFamily: 'var(--font-mono)' }}>{u.attribution.code ?? '—'}</span></Line>
              <Line label="Attributed">{fmtDate(u.attribution.attributedAt)} · {u.attribution.source}</Line>
            </div>
          ) : (
            <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>No creator-code attribution.</span>
          )}
        </Card>
        <Card padding={18}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
            Revenue generated
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gross</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 22 }}>
                {fmtUSD(u.creatorEarnings.lifetimeGrossCents)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Commission</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 22 }}>
                {fmtUSD(u.creatorEarnings.lifetimeCommissionCents)}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
            {u.creatorEarnings.events.length} billing event{u.creatorEarnings.events.length === 1 ? '' : 's'}
          </div>
        </Card>
      </div>

      {/* Subscription timeline */}
      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>
            Subscription timeline
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            RevenueCat webhook events
          </div>
        </div>
        <DataTable
          rows={u.receipts}
          columns={RECEIPT_COLUMNS}
          rowKey={(r) => r.id}
          pageSize={20}
          emptyState="No subscription events."
        />
      </Card>

      {/* Collections */}
      {u.collections.map((c) => (
        <CollectionBlock key={c.id} c={c} />
      ))}
      {u.collections.length === 0 && (
        <Card padding={20}><span style={{ color: 'var(--ink-3)', fontSize: 13 }}>No collections.</span></Card>
      )}
    </>
  );
}

const RECEIPT_COLUMNS: Column<User360Dto['receipts'][number]>[] = [
  {
    key: 'eventType',
    header: 'Event',
    render: (r) => <Pill size="sm">{r.eventType}</Pill>,
  },
  {
    key: 'productId',
    header: 'Product',
    render: (r) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.productId ?? '—'}</span>,
  },
  {
    key: 'store',
    header: 'Store',
    hideMobile: true,
    render: (r) => r.store ?? '—',
  },
  {
    key: 'receivedAt',
    header: 'Received',
    sortKey: (r) => new Date(r.receivedAt).getTime(),
    render: (r) => <span style={{ color: 'var(--ink-2)' }}>{fmtDate(r.receivedAt)}</span>,
  },
];

function CollectionBlock({ c }: { c: User360Collection }) {
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>{c.name}</span>
          {c.isDefault && <Pill size="sm">Default</Pill>}
        </div>
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          {fmtNum(c.cardCount)} cards · <strong style={{ color: 'var(--ink)' }}>{fmtDollars(c.totalValue)}</strong>
        </span>
      </div>
      {c.items.length === 0 ? (
        <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>Empty collection.</span>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: 12,
          }}
        >
          {c.items.map((it) => <ItemCard key={it.itemId} it={it} />)}
        </div>
      )}
    </Card>
  );
}

function ItemCard({ it }: { it: User360CollectionItem }) {
  const grade =
    it.gradingCompany && it.gradingCompany !== 'raw'
      ? `${it.gradingCompany.toUpperCase()} ${it.gradeValue ?? ''}`.trim()
      : it.condition ?? 'Raw';
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        background: 'var(--bg-sunken)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ position: 'relative', aspectRatio: '5 / 7', background: 'var(--bg)' }}>
        {it.imageSmallUrl ? (
          <img src={it.imageSmallUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : null}
        {it.quantity > 1 && (
          <span
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              background: 'var(--ink)',
              color: 'var(--bg-elev)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              padding: '1px 6px',
              borderRadius: 'var(--r-pill)',
            }}
          >
            ×{it.quantity}
          </span>
        )}
      </div>
      <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {it.name ?? it.cardId ?? '—'}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {it.setName ?? ''}{it.number ? ` · #${it.number}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 2 }}>
          <Pill size="sm">{grade}</Pill>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
            {it.marketPrice ? fmtDollars(it.marketPrice) : '—'}
          </span>
        </div>
        {it.variant && (
          <div style={{ fontSize: 10, color: 'var(--ink-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {it.variant}
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <span style={{ width: 90, color: 'var(--ink-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--ink)' }}>{children}</span>
    </div>
  );
}

function fmtShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}
